import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import ExternalUserHandler from 'handlers/external.user.handler';
import type { DriverBehaviorThresholds } from 'types/customer.types';
import { type IUpdateCustomerPayload } from 'types/payloads/customer.payload.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface ThresholdRow {
  id: string;
  key: string;
  description: string;
  value: number;
}

export const DriverBehaviorSettingsView = () => {
  const { t } = useTranslation();
  const externalUserHandler = new ExternalUserHandler();
  const { customer, role } = useAppSelector((state: any) => state.authReducer);
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const currentThresholds = customer.driverBehavior;

  const initialData: ThresholdRow[] = [
    {
      id: 'lowThreshold',
      key: 'Low Threshold',
      description: t('events_per_day_below_this_value_are_considered_normal'),
      value: currentThresholds.lowThreshold,
    },
    {
      id: 'highThreshold',
      key: 'High Threshold',
      description: t('events_per_day_above_this_value_are_considered_critical'),
      value: currentThresholds.highThreshold,
    },
  ];

  const [rowData, setRowData] = useState<ThresholdRow[]>(initialData);
  const [originalData] = useState<ThresholdRow[]>(
    JSON.parse(JSON.stringify(initialData))
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const modalTitleBgColor = enableDarkTheme
    ? palette.toolBarBackground
    : '#f5f5f5';
  const modalTextColor = palette.textPrimary;
  const modalBorderColor = enableDarkTheme ? '#2F445E' : '#e0e0e0';
  const dialogBgColor = enableDarkTheme ? palette.paperBackground : '#ffffff';

  const canEdit =
    role === 'director' ||
    role === 'external-admin' ||
    role === 'internal-user';

  // Update rowData when customer data changes
  useEffect(() => {
    const updatedThresholds = customer.driverBehavior;
    setRowData([
      {
        id: 'lowThreshold',
        key: 'Low Threshold',
        description: t('events_per_day_below_this_value_are_considered_normal'),
        value: updatedThresholds.lowThreshold,
      },
      {
        id: 'highThreshold',
        key: 'High Threshold',
        description: t(
          'events_per_day_above_this_value_are_considered_critical'
        ),
        value: updatedThresholds.highThreshold,
      },
    ]);
  }, [customer.driverBehaviorThresholds]);

  const handleValueChange = (id: string, newValue: string) => {
    if (!canEdit) return;

    const numValue = newValue === '' ? 0 : Number(newValue);
    if (!isNaN(numValue) && numValue >= 0) {
      setRowData((prevData) =>
        prevData.map((row) =>
          row.id === id ? { ...row, value: numValue } : row
        )
      );
    }
  };

  const handleResetChanges = () => {
    if (!canEdit) return;
    setRowData(JSON.parse(JSON.stringify(originalData)));
  };

  const handleSaveClick = () => {
    if (!canEdit) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const thresholds: DriverBehaviorThresholds = {
        lowThreshold: rowData.find((r) => r.id === 'lowThreshold')?.value ?? 0,
        highThreshold:
          rowData.find((r) => r.id === 'highThreshold')?.value ?? 0,
      };

      if (thresholds.highThreshold <= thresholds.lowThreshold) {
        setShowConfirmDialog(false);
        setIsSaving(false);
        return;
      }

      const customerPayload: IUpdateCustomerPayload = {
        customerName: customer.name,
        contactFirstName: customer.contactFirstName,
        contactLastName: customer.contactLastName,
        email: customer.email,
        phoneNumber: customer.phoneNumber,
        internalAccountingLink:
          customer.internalAccountingLink === null
            ? ''
            : customer.internalAccountingLink,
        unitSystem: customer.unitSystem,
        preferredTimeDisplayMode: customer.preferredTimeDisplayMode,
        driverBehavior: thresholds,
      };

      await externalUserHandler.updateCurrentCustomer(customerPayload);
      setShowConfirmDialog(false);
    } catch (err: any) {
      console.error('Error updating thresholds:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);
  };

  const hasChanges = JSON.stringify(rowData) !== JSON.stringify(originalData);

  const lowThreshold = rowData.find((r) => r.id === 'lowThreshold')?.value ?? 0;
  const highThreshold =
    rowData.find((r) => r.id === 'highThreshold')?.value ?? 0;
  const isValid = highThreshold > lowThreshold;
  const canSaveChanges = canEdit && hasChanges && isValid;

  const changes = rowData
    .map((row) => {
      const original = originalData.find((o) => o.id === row.id);
      if (original && row.value !== original.value) {
        return {
          key: row.key,
          from: original.value,
          to: row.value,
        };
      }
      return null;
    })
    .filter(Boolean);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          padding: '10px',
          paddingBottom: '20px',
          borderRadius: '4px',
          marginTop: '10px',
          border: `1px solid ${modalBorderColor}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: modalTextColor,
            fontWeight: 600,
            paddingLeft: '10px',
          }}
        >
          {t('driver_behavior_thresholds')}
        </Typography>
      </Box>
      <Box
        sx={{
          marginTop: '20px',
          border: `1px solid ${modalBorderColor}`,
          borderRadius: '4px',
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: modalTitleBgColor,
                    color: modalTextColor,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: `1px solid ${modalBorderColor}`,
                    width: '40%',
                  }}
                >
                  {t('threshold_type')}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: modalTitleBgColor,
                    color: modalTextColor,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: `1px solid ${modalBorderColor}`,
                    width: '30%',
                  }}
                >
                  {t('description')}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: modalTitleBgColor,
                    color: modalTextColor,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: `1px solid ${modalBorderColor}`,
                    width: '30%',
                  }}
                >
                  {t('grade_importer_quality_col_value')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.02)'
                        : 'rgba(0,0,0,0.02)',
                    },
                    '&:hover': {
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.04)',
                    },
                    height: '45px',
                  }}
                >
                  <TableCell
                    sx={{
                      color: modalTextColor,
                      borderBottom: `1px solid ${modalBorderColor}`,
                      fontSize: '0.875rem',
                      padding: '8px 16px',
                    }}
                  >
                    {row.key}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: modalTextColor,
                      borderBottom: `1px solid ${modalBorderColor}`,
                      fontSize: '0.875rem',
                      padding: '8px 16px',
                    }}
                  >
                    {row.description}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: modalTextColor,
                      borderBottom: `1px solid ${modalBorderColor}`,
                      fontSize: '0.875rem',
                      padding: '8px 16px',
                    }}
                  >
                    {canEdit ? (
                      <TextField
                        size="small"
                        type="number"
                        value={row.value}
                        onChange={(e) => {
                          handleValueChange(row.id, e.target.value);
                        }}
                        inputProps={{ min: 0, step: 1 }}
                        error={
                          row.id === 'highThreshold' &&
                          !isValid &&
                          highThreshold <= lowThreshold
                        }
                        sx={{
                          minWidth: 200,
                          '& .MuiOutlinedInput-root': {
                            color: modalTextColor,
                            '& fieldset': {
                              borderColor: modalBorderColor,
                            },
                            '&:hover fieldset': {
                              borderColor: modalTextColor,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: palette.primary,
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: modalTextColor,
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          color: modalTextColor,
                          fontSize: '0.875rem',
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: '14px',
                        }}
                      >
                        {row.value}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!isValid && (
          <Box sx={{ p: 2 }}>
            <Typography
              sx={{
                color: enableDarkTheme ? '#ef4444' : '#dc2626',
                fontSize: '0.875rem',
              }}
            >
              {t('high_threshold_must_be_greater_than_low_threshold')}
            </Typography>
          </Box>
        )}

        {canEdit && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              padding: '16px 24px',
              borderTop: `1px solid ${modalBorderColor}`,
              backgroundColor: modalTitleBgColor,
            }}
          >
            <Button
              onClick={handleResetChanges}
              variant="outlined"
              color="error"
              disabled={!hasChanges}
              sx={{ minWidth: '100px' }}
            >
              {t('reset_changes')}
            </Button>
            <Button
              onClick={handleSaveClick}
              variant="contained"
              color="success"
              disabled={!canSaveChanges}
              sx={{ minWidth: '100px' }}
            >
              {t('save_all_changes')}
            </Button>
          </Box>
        )}
      </Box>

      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelSave}
        PaperProps={{
          sx: {
            backgroundColor: dialogBgColor,
            color: modalTextColor,
            border: `1px solid ${modalBorderColor}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: modalTitleBgColor,
            borderBottom: `1px solid ${modalBorderColor}`,
            padding: '12px 24px',
            fontSize: '1.25rem',
            fontWeight: 500,
            color: modalTextColor,
          }}
        >
          {t('confirm_save_changes')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: modalTextColor, marginBottom: 2, marginTop: 2 }}
          >
            {t('you_are_about_to_save_the_following_threshold_changes')}
          </DialogContentText>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {changes.map((change: any, index) => (
              <Box
                key={index}
                sx={{
                  padding: '8px 12px',
                  marginBottom: '8px',
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.05)',
                  borderRadius: '4px',
                  border: `1px solid ${modalBorderColor}`,
                }}
              >
                <Box
                  sx={{
                    fontWeight: 600,
                    color: modalTextColor,
                    fontSize: '0.875rem',
                  }}
                >
                  {change.key}
                </Box>
                <Box
                  sx={{
                    color: modalTextColor,
                    fontSize: '0.8rem',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <span
                    style={{
                      color: enableDarkTheme ? '#ef4444' : '#dc2626',
                      textDecoration: 'line-through',
                    }}
                  >
                    {change.from}
                  </span>
                  <span>{t('x2192')}</span>
                  <span
                    style={{
                      color: enableDarkTheme ? '#22c55e' : '#16a34a',
                      fontWeight: 600,
                    }}
                  >
                    {change.to}
                  </span>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelSave}
            color="error"
            sx={{ color: modalTextColor }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmSave}
            color="success"
            variant="contained"
            autoFocus
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
